import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { EmailGuard, AuthGuard } from "../App";
import { FaTools, FaHome, FaUser, FaInfoCircle, FaCheckCircle, FaHourglassHalf, FaTimesCircle, FaSyncAlt } from 'react-icons/fa';
import { Oval } from "react-loader-spinner"; // Assuming this is available
import { StandardURL } from "../App";

const MaintenanceRequest = () => {
    const [maintenanceRequests, setMaintenanceRequests] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchMaintenanceRequests();
    }, []);

    const getStatusBadge = (status) => {
        const lowerStatus = status ? status.toLowerCase() : 'unknown';
        let colorClass = 'bg-gray-100 text-gray-800';
        let icon = <FaInfoCircle className="w-3 h-3 mr-1" />;

        switch (lowerStatus) {
            case 'pending':
                colorClass = 'bg-yellow-100 text-yellow-800';
                icon = <FaHourglassHalf className="w-3 h-3 mr-1" />;
                break;
            case 'in progress':
                colorClass = 'bg-blue-100 text-blue-800';
                icon = <FaTools className="w-3 h-3 mr-1" />;
                break;
            case 'completed':
                colorClass = 'bg-green-100 text-green-800';
                icon = <FaCheckCircle className="w-3 h-3 mr-1" />;
                break;
            case 'cancelled':
                colorClass = 'bg-red-100 text-red-800';
                icon = <FaTimesCircle className="w-3 h-3 mr-1" />;
                break;
            default:
                // default handles 'unknown' and other statuses
                break;
        }

        return (
            <span className={`inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full capitalize ${colorClass}`}>
                {icon} {status}
            </span>
        );
    };

    const fetchMaintenanceRequests = async () => {
        setLoading(true);
        const token = localStorage.getItem("token");
        try {
            const response = await axios.get(
                `${StandardURL}/iNyumba/maintenance/maintenancerequest/all`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            // Ensure response.data.response is an array before setting
            if (Array.isArray(response.data.response)) {
                 // Reverse the array to show the newest requests first
                setMaintenanceRequests(response.data.response.reverse());
            } else {
                setMaintenanceRequests([]);
            }
           
        } catch (error) {
            toast.error(error.response?.data?.response || "Failed to fetch maintenance requests.");
            setMaintenanceRequests([]);
        } finally {
            setLoading(false);
        }
    };

    return (
        <EmailGuard>
            <AuthGuard>
                <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
                    <div className="max-w-6xl mx-auto">
                        {/* Header and Refresh Button */}
                        <div className="flex justify-between items-center mb-8">
                            <h1 className="text-3xl font-extrabold text-gray-900 flex items-center">
                                <FaTools className="mr-3 text-indigo-600" /> All Maintenance Requests
                            </h1>
                            <button
                                onClick={fetchMaintenanceRequests}
                                disabled={loading}
                                className="inline-flex items-center bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded-lg transition duration-150 shadow-md disabled:bg-indigo-400"
                            >
                                <FaSyncAlt className={`mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh Data
                            </button>
                        </div>

                        {/* Loading State */}
                        {loading && (
                            <div className="flex flex-col items-center justify-center p-12 bg-white rounded-xl shadow-xl">
                                <Oval color="#4f46e5" secondaryColor="#a5b4fc" height={50} width={50} ariaLabel="loading-spinner" />
                                <p className="mt-4 text-gray-600">Fetching all maintenance requests...</p>
                            </div>
                        )}

                        {/* Data Table Container */}
                        {!loading && (
                            <div className="bg-white shadow-xl rounded-xl overflow-hidden border-t-4 border-indigo-500">
                                {maintenanceRequests.length === 0 ? (
                                    <div className="text-center p-12">
                                        <FaTools className="mx-auto h-12 w-12 text-gray-400" />
                                        <h3 className="mt-2 text-lg font-medium text-gray-900">No Requests Found</h3>
                                        <p className="mt-1 text-sm text-gray-500">
                                            There are no maintenance requests at the moment.
                                        </p>
                                    </div>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-100">
                                                <tr>
                                                    <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                                        Req. ID
                                                    </th>
                                                    <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                                        <FaUser className="inline mr-1" /> User ID
                                                    </th>
                                                    <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                                        <FaHome className="inline mr-1" /> Property/House
                                                    </th>
                                                    <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                                        Description
                                                    </th>
                                                    <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                                        Status
                                                    </th>
                                                    <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                                        Created At
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {maintenanceRequests.map((request) => (
                                                    <tr key={request.request_id || request.id} className="hover:bg-gray-50 transition duration-100">
                                                        <td className="py-3 px-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                            {request.request_id}
                                                        </td>
                                                        <td className="py-3 px-4 whitespace-nowrap text-sm text-gray-600">
                                                            {request.user_id}
                                                        </td>
                                                        <td className="py-3 px-4 whitespace-nowrap text-sm text-gray-600">
                                                            {request.property_id} / {request.house_no}
                                                        </td>
                                                        <td className="py-3 px-4 text-sm text-gray-700 max-w-xs overflow-hidden text-ellipsis">
                                                            {request.request_content}
                                                        </td>
                                                        <td className="py-3 px-4 whitespace-nowrap">
                                                            {getStatusBadge(request.status)}
                                                        </td>
                                                        <td className="py-3 px-4 whitespace-nowrap text-sm text-gray-500">
                                                            {new Date(request.created_at).toLocaleString()}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </AuthGuard>
        </EmailGuard>
    );
};

export default MaintenanceRequest;